import { FC, useEffect, useState } from "react";

import { ContentCopy, ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  M2mClient,
  listM2mClient,
  postM2mClientRotate,
  postM2mClientRotateCancel,
  postM2mClientRotateStart,
  putM2mClient,
} from "@rubi/api-generated";

const DEFAULT_SCOPES = ["get_reports_xml"];

export const M2mKeysPanel: FC<{ orgId: string }> = ({ orgId }) => {
  const [clients, setClients] = useState<M2mClient[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Create form
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [scopesText, setScopesText] = useState(DEFAULT_SCOPES.join(", "));
  const [rubiApiId, setRubiApiId] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createdCreds, setCreatedCreds] = useState<{
    client_id: string;
    client_secret: string;
  } | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const response = await listM2mClient({ query: { org_id: orgId } });
      setClients(response.data!.clients);
      setLoadError(null);
    } catch (e) {
      setLoadError(
        e instanceof Error ? e.message : "Failed to load M2M clients",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // re-run on org change
  }, [orgId]);

  const handleCreate = async () => {
    if (!name.trim() || !description.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      const scopes = scopesText
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      const response = await putM2mClient({
        body: {
          client_name: name.trim(),
          client_description: description.trim(),
          org_id: orgId,
          scopes: scopes.length > 0 ? scopes : null,
          rubi_api_id: rubiApiId.trim() || null,
        },
      });
      const data = response.data!;
      setCreatedCreds({
        client_id: data.client_id,
        client_secret: data.client_secret,
      });
      setName("");
      setDescription("");
      setScopesText(DEFAULT_SCOPES.join(", "));
      setRubiApiId("");
      await load();
    } catch (e) {
      setCreateError(
        e instanceof Error ? e.message : "Failed to create M2M client",
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Create new M2M client
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Issues Stytch credentials scoped to this org. The client secret is
          shown <strong>once</strong> on creation — copy it before closing.
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Client name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="rubi_api_id (optional)"
              value={rubiApiId}
              onChange={(e) => setRubiApiId(e.target.value)}
              helperText="Webhook origin host identifier (leave blank if unsure)"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Scopes (comma or newline separated)"
              value={scopesText}
              onChange={(e) => setScopesText(e.target.value)}
              helperText={`Defaults to "${DEFAULT_SCOPES.join(", ")}" if blank`}
            />
          </Grid>
        </Grid>

        {createError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {createError}
          </Alert>
        )}

        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={
              creating || name.trim() === "" || description.trim() === ""
            }
          >
            {creating ? "Creating…" : "Create M2M client"}
          </Button>
        </Stack>
      </Paper>

      <Box>
        <Typography variant="h6" gutterBottom>
          Existing M2M clients
        </Typography>
        {loadError && <Alert severity="error">{loadError}</Alert>}
        {loading && <Typography variant="body2">Loading…</Typography>}
        {!loading && clients.length === 0 && !loadError && (
          <Typography variant="body2" color="text.secondary">
            No M2M clients yet.
          </Typography>
        )}
        <Stack spacing={1}>
          {clients.map((c) => (
            <ClientRow key={c.id} client={c} onRotated={load} />
          ))}
        </Stack>
      </Box>

      <CredentialsDialog
        creds={createdCreds}
        onClose={() => setCreatedCreds(null)}
      />
    </Stack>
  );
};

const ClientRow: FC<{
  client: M2mClient;
  onRotated: () => void;
}> = ({ client, onRotated }) => {
  const [nextSecret, setNextSecret] = useState<string | null>(null);
  const [rotateError, setRotateError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const startRotate = async () => {
    setBusy(true);
    setRotateError(null);
    try {
      const response = await postM2mClientRotateStart({
        path: { client_id: client.client_id },
        body: {},
      });
      const data = response.data!;
      setNextSecret(data.next_client_secret);
    } catch (e) {
      setRotateError(e instanceof Error ? e.message : "Rotate start failed");
    } finally {
      setBusy(false);
    }
  };

  const completeRotate = async () => {
    setBusy(true);
    setRotateError(null);
    try {
      await postM2mClientRotate({
        path: { client_id: client.client_id },
        body: {},
      });
      setNextSecret(null);
      onRotated();
    } catch (e) {
      setRotateError(e instanceof Error ? e.message : "Rotate complete failed");
    } finally {
      setBusy(false);
    }
  };

  const cancelRotate = async () => {
    setBusy(true);
    setRotateError(null);
    try {
      await postM2mClientRotateCancel({
        path: { client_id: client.client_id },
        body: {},
      });
      setNextSecret(null);
    } catch (e) {
      setRotateError(e instanceof Error ? e.message : "Rotate cancel failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <Typography sx={{ flex: 1 }}>{client.client_name}</Typography>
          <Chip
            size="small"
            label={`…${client.client_secret_last_four}`}
            sx={{ fontFamily: "monospace" }}
          />
          <Typography variant="caption" color="text.secondary">
            {new Date(client.time_created).toLocaleDateString()}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1}>
          <CopyableField label="client_id" value={client.client_id} />
          <Typography variant="body2">
            <strong>Description:</strong> {client.client_description || "—"}
          </Typography>
          <Typography variant="body2">
            <strong>Scopes:</strong>{" "}
            {client.scopes.length === 0 ? "—" : client.scopes.join(", ")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            internal id {client.id} · created {client.time_created}
          </Typography>

          {rotateError && <Alert severity="error">{rotateError}</Alert>}

          {nextSecret == null ? (
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={startRotate} disabled={busy}>
                Start secret rotation
              </Button>
            </Stack>
          ) : (
            <Box>
              <Alert severity="warning" sx={{ mb: 1 }}>
                New secret generated. Copy it now, then deploy it to the client
                before completing the rotation — until you complete, both the
                old and new secret work.
              </Alert>
              <CopyableField label="next_client_secret" value={nextSecret} />
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={completeRotate}
                  disabled={busy}
                >
                  Complete rotation (invalidates old secret)
                </Button>
                <Button size="small" onClick={cancelRotate} disabled={busy}>
                  Cancel rotation
                </Button>
              </Stack>
            </Box>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const CopyableField: FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <TextField
      label={label}
      value={value}
      size="small"
      fullWidth
      InputProps={{ readOnly: true, sx: { fontFamily: "monospace" } }}
    />
    <IconButton
      size="small"
      onClick={() => navigator.clipboard.writeText(value)}
      title="Copy"
    >
      <ContentCopy fontSize="small" />
    </IconButton>
  </Stack>
);

const CredentialsDialog: FC<{
  creds: { client_id: string; client_secret: string } | null;
  onClose: () => void;
}> = ({ creds, onClose }) => (
  <Dialog open={creds != null} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>M2M client created</DialogTitle>
    <DialogContent>
      <DialogContentText sx={{ mb: 2 }}>
        <strong>Copy the client secret now.</strong> It will not be retrievable
        again — only the last four characters will be visible.
      </DialogContentText>
      <Stack spacing={2}>
        <CopyableField label="client_id" value={creds?.client_id ?? ""} />
        <CopyableField
          label="client_secret"
          value={creds?.client_secret ?? ""}
        />
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} variant="contained">
        I&apos;ve saved it
      </Button>
    </DialogActions>
  </Dialog>
);
